import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, setConsent, applyConsent, trackEvent } from "@/lib/tracking";

const DENIED = { necessary: true, analytics: false, marketing: false, functional: false };

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DENIED);

  // First mount: decide whether to show the banner, and apply any saved consent
  // (or apply DENIED so non-essential scripts stay off until the visitor chooses).
  useEffect(() => {
    const c = getConsent();
    if (c) {
      setPrefs({ ...DENIED, ...c });
      applyConsent(c);
    } else {
      applyConsent(DENIED);
      setVisible(true);
    }
  }, []);

  // Allow re-opening from anywhere (footer "Cookie Preferences" link).
  useEffect(() => {
    const handler = () => {
      const c = getConsent();
      setPrefs(c ? { ...DENIED, ...c } : DENIED);
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("xa:open-cookie-prefs", handler);
    return () => window.removeEventListener("xa:open-cookie-prefs", handler);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    const c = { necessary: true, analytics: true, marketing: true, functional: true };
    setConsent(c);
    trackEvent("consent_accept_all", c);
    setVisible(false);
  };
  const rejectAll = () => {
    setConsent(DENIED);
    trackEvent("consent_reject_all", DENIED);
    setVisible(false);
  };
  const saveCustom = () => {
    setConsent(prefs);
    trackEvent("consent_save_preferences", prefs);
    setVisible(false);
  };

  const ROWS = [
    {
      k: "necessary",
      label: "Strictly necessary",
      disabled: true,
      note: "Required for the site to function (security, language, consent state).",
    },
    {
      k: "analytics",
      label: "Analytics",
      note: "Anonymous product analytics (PostHog) so we can understand which pages and content help visitors.",
    },
    {
      k: "marketing",
      label: "Marketing",
      note: "Not currently active. Reserved for any future advertising or remarketing tags.",
    },
    {
      k: "functional",
      label: "Functional",
      note: "Remembers in-page preferences and the AI chat conversation context.",
    },
  ];

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-4 inset-x-4 md:bottom-6 md:right-6 md:left-auto md:max-w-md z-[60] bg-[#07111F] text-[#F7F9FC] border border-white/10 rounded-xl shadow-[0_24px_60px_rgba(0,0,0,0.45)] overflow-hidden"
    >
      <div className="p-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#00D4FF] mb-2">Cookies &amp; privacy</div>
        <h3 id="cookie-banner-title" className="font-display text-lg font-semibold mb-2">
          We respect how you&apos;re tracked.
        </h3>
        <p className="text-sm text-white/70 leading-relaxed">
          Strictly necessary cookies are always on. Analytics, marketing, and functional cookies
          stay off until you opt in. You can change your choice anytime via the
          {" "}<strong className="text-white">Cookie Preferences</strong> link in the footer.{" "}
          See our <Link to="/cookie-policy" className="underline hover:text-[#00D4FF]">Cookie Policy</Link>.
        </p>

        {showPrefs && (
          <div className="mt-5 space-y-4 border-t border-white/10 pt-5 text-sm">
            {ROWS.map((row) => (
              <label key={row.k} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`cookie-pref-${row.k}`}
                  checked={!!prefs[row.k]}
                  disabled={row.disabled}
                  onChange={(e) => setPrefs((p) => ({ ...p, [row.k]: e.target.checked }))}
                  className="mt-1 accent-[#6D4DFF] disabled:opacity-50"
                />
                <span>
                  <span className="text-white">{row.label}{row.disabled && <span className="ml-2 text-[10px] uppercase tracking-wider text-white/50">Always on</span>}</span>
                  <span className="block text-xs text-white/55 mt-0.5 leading-relaxed">{row.note}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <button data-testid="cookie-accept-all" onClick={acceptAll} className="xa-btn xa-btn-primary text-sm">
            Accept all
          </button>
          <button data-testid="cookie-reject-all" onClick={rejectAll} className="xa-btn xa-btn-ghost-dark text-sm">
            Reject all
          </button>
          {showPrefs ? (
            <button data-testid="cookie-save" onClick={saveCustom} className="xa-btn xa-btn-ghost-dark text-sm">
              Save preferences
            </button>
          ) : (
            <button data-testid="cookie-manage" onClick={() => setShowPrefs(true)} className="xa-btn xa-btn-ghost-dark text-sm">
              Manage preferences
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
